// Utility functions for handling URLs

import { VIDEO_EXTENSIONS } from "./validateFileUpload";

// Regex for URLs including http/https scheme
const urlWithHttpPattern =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,10}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

// Regex for URLs that might be missing the scheme (e.g., example.com)
const urlWithoutHttpPattern =
  /^[-a-zA-Z0-9:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,10}\b(?:[-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

/**
 * Validates if a string is a plausible URL structure using regex.
 * Checks for patterns with or without http(s):// prefix.
 *
 * @param url The string to validate.
 * @returns True if the string matches a common URL pattern, false otherwise.
 */
export const isValidUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  const trimmedUrl = url.trim();
  // Check against both patterns
  return (
    urlWithHttpPattern.test(trimmedUrl) ||
    urlWithoutHttpPattern.test(trimmedUrl)
  );
};

/**
 * Ensures a URL string starts with http:// or https://.
 * If the URL doesn't have a scheme, it prepends "https://".
 *
 * @param url The URL string.
 * @returns The URL string with https:// prepended if necessary, or the original string.
 */
export const ensureUrlScheme = (url: string | undefined | null): string => {
  if (!url) return ""; // Return empty string for null/undefined/empty input
  const trimmedUrl = url.trim();
  if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }
  return trimmedUrl;
};

/**
 * Checks if a given URL points to an image file based on its extension.
 * @param url The URL string to check.
 * @returns True if the URL likely points to an image, false otherwise.
 */
export const isImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const extension = url.split(".").pop()?.toLowerCase() || "";
  // Assume it's an image if the extension is not in the video list
  return !VIDEO_EXTENSIONS.includes(extension);
};
